import { Col, Row } from 'react-bootstrap';
import { Field, FieldArray } from 'redux-form';
import { and, not, ifElse, equals, always } from 'ramda';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Divider } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { FormattedMessage } from 'react-intl';
import messages from './messages';
import {
  renderTextField,
  renderEmailField,
  renderSelectField,
  renderPhoneFieldWithPermanentWarn,
  phoneParser,
  phoneFormatter,
  renderRadioButton,
} from '../../utils/redux-form-helper';
import { useRef, useMemo, useEffect } from 'react';
import { validatePhoneNumber, validateEmail } from '../../utils/validations';

const generatePhoneTypes = (intl) => {
  const phoneTypes = ['mobile', 'work', 'other'].map((type) => ({
    value: type,
    text: intl.formatMessage(
      messages[`phoneType${type.charAt(0).toUpperCase() + type.slice(1)}`],
    ),
  }));
  return phoneTypes;
};

const generateEmailTypes = (intl) => {
  const emailTypes = ['personal', 'work', 'other'].map((type) => ({
    value: type,
    text: intl.formatMessage(
      messages[`emailType${type.charAt(0).toUpperCase() + type.slice(1)}`],
    ),
  }));
  return emailTypes;
};

const DeleteButton = ({ onClick }) => (
  <div className="row-remove-btn">
    <a onClick={onClick} style={{ cursor: 'pointer' }}>
      <i className="et-trash" />
    </a>
  </div>
);

const AddContactButton = ({ onClick, message }) => (
  <a className="btn-text" onClick={onClick}>
    <FormattedMessage {...message} />
  </a>
);

const PhoneFields = ({
  fields,
  field,
  disableFields,
  leaseLocked,
  isProspectMode,
  intl,
  change,
  required,
  contactId,
}) => {
  const maxFields = 3;
  const phoneTypes = generatePhoneTypes(intl);

  // Memoize validation function to prevent infinite re-renders
  const validatePhone = useMemo(
    () => createPhoneValidator(intl, required),
    [intl, required],
  );

  // Ensure first item is always primary when fields change
  useEffect(() => {
    if (fields.length > 0) {
      // Check if first item needs to be updated to primary
      const firstField = fields.get(0);
      if (firstField && !firstField.isPrimary) {
        change(`${field}.phones[0].isPrimary`, true);
      }

      // Ensure all other items are not primary
      for (let i = 1; i < fields.length; i++) {
        const currentField = fields.get(i);
        if (currentField && currentField.isPrimary) {
          change(`${field}.phones[${i}].isPrimary`, false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.length]);

  const addPhone = () => {
    if (fields.length < maxFields) {
      fields.push({
        phoneNumber: '',
        phoneType: 'mobile',
        isPrimary: fields.length === 0, // First item is primary
        isValidated: false,
        ...(contactId && { contactId }), // Include contactId if it exists
      });
    }
  };

  const handleRemove = (index) => {
    fields.remove(index);
    // After removal, ensure first item (if exists) is primary
    if (fields.length > 0 && index === 0) {
      const firstField = fields.get(0);
      if (firstField && !firstField.isPrimary) {
        change(`${field}.phones[0].isPrimary`, true);
      }
    }
  };

  return (
    <div>
      {fields.map((phone, index) => {
        const canDelete = fields.length > 1;
        return (
          <div key={index}>
            <div
              style={{ display: 'flex', alignItems: 'flex-start', gap: '5px' }}
            >
              <div style={{ flex: '5' }}>
                <Field
                  name={`${phone}.phoneNumber`}
                  component={renderPhoneFieldWithPermanentWarn}
                  parse={phoneParser}
                  format={phoneFormatter}
                  disabled={disableFields || leaseLocked}
                  placeholder={`Phone ${index + 1}`}
                  validate={validatePhone}
                />
              </div>
              <div style={{ flex: '5' }}>
                <Field
                  name={`${phone}.phoneType`}
                  component={renderSelectField}
                  options={phoneTypes}
                  disabled={disableFields || leaseLocked}
                />
                {/* Invisible placeholder to maintain alignment when validation errors appear */}
                <div style={{ height: '21px', visibility: 'hidden' }}>
                  &nbsp;
                </div>
              </div>
              <div
                style={{
                  flex: '1',
                  display: 'flex',
                  alignItems: 'center',
                  height: '34px',
                }}
              >
                {canDelete && (
                  <DeleteButton onClick={() => handleRemove(index)} />
                )}
              </div>
            </div>
          </div>
        );
      })}
      {fields.length < maxFields && !(disableFields || leaseLocked) && (
        <div>
          <AddContactButton onClick={addPhone} message={messages.addPhone} />
        </div>
      )}
    </div>
  );
};

const EmailFields = ({
  fields,
  field,
  disableFields,
  leaseLocked,
  isProspectMode,
  isCreateApplicationMode,
  intl,
  change,
  required,
  contactId,
}) => {
  const maxFields = 3;
  const emailTypes = generateEmailTypes(intl);

  // Memoize validation function to prevent infinite re-renders
  const validateEmail = useMemo(
    () => createEmailValidator(intl, required),
    [intl, required],
  );

  // Ensure first item is always primary when fields change
  useEffect(() => {
    if (fields.length > 0) {
      // Check if first item needs to be updated to primary
      const firstField = fields.get(0);
      if (firstField && !firstField.isPrimary) {
        change(`${field}.emails[0].isPrimary`, true);
      }

      // Ensure all other items are not primary
      for (let i = 1; i < fields.length; i++) {
        const currentField = fields.get(i);
        if (currentField && currentField.isPrimary) {
          change(`${field}.emails[${i}].isPrimary`, false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.length]);

  const addEmail = () => {
    if (fields.length < maxFields) {
      fields.push({
        emailAddress: '',
        emailType: 'personal',
        isPrimary: fields.length === 0, // First item is primary
        isValidated: false,
        ...(contactId && { contactId }), // Include contactId if it exists
      });
    }
  };

  const handleRemove = (index) => {
    fields.remove(index);
    // After removal, ensure first item (if exists) is primary
    if (fields.length > 0 && index === 0) {
      const firstField = fields.get(0);
      if (firstField && !firstField.isPrimary) {
        change(`${field}.emails[0].isPrimary`, true);
      }
    }
  };

  return (
    <div>
      {fields.map((email, index) => {
        const canDelete = fields.length > 1;
        return (
          <div key={index}>
            <div
              style={{ display: 'flex', alignItems: 'flex-start', gap: '5px' }}
            >
              <div style={{ flex: '5' }}>
                <Field
                  name={`${email}.emailAddress`}
                  component={renderEmailField}
                  maxLength="70"
                  disabled={disableFields || leaseLocked}
                  placeholder={`Email ${index + 1}`}
                  validate={validateEmail}
                />
              </div>
              <div style={{ flex: '5' }}>
                <Field
                  name={`${email}.emailType`}
                  component={renderSelectField}
                  options={emailTypes}
                  disabled={disableFields || leaseLocked}
                />
                {/* Invisible placeholder to maintain alignment when validation errors appear */}
                <div style={{ height: '21px', visibility: 'hidden' }}>
                  &nbsp;
                </div>
              </div>
              <div
                style={{
                  flex: '1',
                  display: 'flex',
                  alignItems: 'center',
                  height: '34px',
                }}
              >
                {canDelete && (
                  <DeleteButton onClick={() => handleRemove(index)} />
                )}
              </div>
            </div>
          </div>
        );
      })}
      {fields.length < maxFields && !(disableFields || leaseLocked) && (
        <div>
          <AddContactButton onClick={addEmail} message={messages.addEmail} />
        </div>
      )}
    </div>
  );
};

/**
 * Factory function to create field validators with shared logic
 * @param {Object} intl - Internationalization object
 * @param {boolean} required - Whether the first field is required
 * @param {Function} formatValidator - Function to validate the format (e.g., validatePhoneNumber, validateEmail)
 * @param {Object} errorMessage - Error message object for invalid format
 * @param {RegExp} indexPattern - Regex pattern to extract the field index from the field name
 * @returns {Function} Validator function
 */
const createFieldValidator =
  (intl, required, formatValidator, errorMessage, indexPattern) =>
  (value, allValues, props, name) => {
    // Extract field index from field name using the provided pattern
    // This ensures we match the correct array index (e.g., phones[1] or emails[2])
    const match = name.match(indexPattern);
    const fieldIndex = match ? parseInt(match[1]) : 0;
    const isFirstField = fieldIndex === 0;

    // Validate required for first field only
    if (isFirstField && required && !value) {
      return intl.formatMessage(messages.required);
    }

    // Validate format if value exists
    if (value && !formatValidator(value)) {
      return intl.formatMessage(errorMessage);
    }

    return undefined;
  };

const createPhoneValidator = (intl, required) =>
  createFieldValidator(
    intl,
    required,
    validatePhoneNumber,
    messages.invalidPhoneNumber,
    /\.phones\[(\d+)\]/,
  );

const createEmailValidator = (intl, required) =>
  createFieldValidator(
    intl,
    required,
    validateEmail,
    messages.invalidEmail,
    /\.emails\[(\d+)\]/,
  );

type Props = {
  isProspect: boolean,
  isNew: boolean,
  field: string,
  type: string,
  index: string,
  id: string,
  primaryApplicantId: string,
  onDelete: Function,
  onPrimaryChange: Function,
  suffixList: Array<Object>,
  relationshipList: Array<Object>,
  affordableRelationship: Array<Object>,
  applicantTypeList: Array<Object>,
  showApplicantType: boolean,
  fieldsReadOnly: boolean,
  showEditButton: boolean,
  onEdit: Function,
  isAffordable: boolean,
  isCreateApplicationMode: boolean,
  isProspectMode: boolean,
  leaseLocked: boolean,
  prevPrimaryId: string,
  adultInitialValues: Object,
  affordableRelationshipBundle: Object,
  guarantorOptions: Object,
  liveInCaretakerOptions: Object,
  change: Function,
  currentHOHIndexes: Array<number>,
  isEditMode: boolean,
  intl: Object,
  contactId: string | null,
};

const Adult = ({
  isNew,
  field,
  type,
  suffixList,
  relationshipList,
  applicantTypeList,
  onDelete,
  showApplicantType,
  fieldsReadOnly,
  showEditButton,
  onEdit,
  isAffordable,
  showAffordableRelation = true,
  onPrimaryChange,
  index,
  id,
  primaryApplicantId,
  isProspect,
  isCreateApplicationMode,
  isProspectMode,
  leaseLocked,
  prevPrimaryId,
  affordableRelationshipBundle,
  guarantorOptions,
  liveInCaretakerOptions,
  adultInitialValues,
  change,
  currentHOHIndexes,
  isEditMode,
  intl,
  contactId,
}: Props) => {
  const {
    additionalPhoneAndEmailFieldsForLeadProfile,
    affordableNonAffordableProspect,
  } = useFlags();
  const disableFields = and(not(isNew), fieldsReadOnly);
  const prevTypeRef = useRef(adultInitialValues?.type);

  const isPrimaryApplicant = primaryApplicantId === id;
  const localIsProspect = isCreateApplicationMode
    ? isProspect
    : isPrimaryApplicant;
  const applicantTypeText =
    applicantTypeList.find((a) => a.value === type)?.text?.toLowerCase() || '';
  const disablePrimaryCheckbox = applicantTypeText.includes('guarantor');

  const isPreviousPrimary = prevPrimaryId === id;

  const isThisHOH = currentHOHIndexes.includes(id);

  const filteredApplicantTypeList = applicantTypeList
    .filter((typeList) =>
      localIsProspect
        ? typeof typeList.financiallyResponsible === 'undefined' ||
          typeList.financiallyResponsible
        : true,
    )
    .map((typeList) => {
      const isGuarantor = typeList.text?.toLowerCase().includes('guarantor');
      const shouldDisable = isGuarantor && isThisHOH;
      return {
        ...typeList,
        disabled: shouldDisable || typeList.disabled || false,
      };
    });

  const applicantType = {
    ...(applicantTypeList.find((apt) => apt.value === type) || {}),
  };
  // $FlowFixMe
  const isFinancial = applicantType.financiallyResponsible;
  const isGuarantorOrLiveInCaretakerLeaseSigner = [
    guarantorOptions?.applicantType?.id,
    liveInCaretakerOptions?.applicantType?.id,
  ].includes(applicantType.value);

  const onTypeChange = (event) => {
    const value = event.target.value;
    const isGuarantorLeaseSignerType =
      value === guarantorOptions?.applicantType?.id;
    const isLiveInCaretakerType =
      value === liveInCaretakerOptions?.applicantType?.id;

    const options = isGuarantorLeaseSignerType
      ? guarantorOptions
      : isLiveInCaretakerType
      ? liveInCaretakerOptions
      : {};
    if (isGuarantorLeaseSignerType || isLiveInCaretakerType) {
      change(`${field}.relationship`, options?.relationship?.id);
      change(
        `${field}.affordableRelationship`,
        options?.affordableRelationship?.id,
      );
    }

    const isPrevTypeGuarantorOrLiveInCaretaker = [
      guarantorOptions?.applicantType?.id,
      liveInCaretakerOptions?.applicantType?.id,
    ].includes(prevTypeRef.current);

    if (
      isPrevTypeGuarantorOrLiveInCaretaker &&
      !(isGuarantorLeaseSignerType || isLiveInCaretakerType)
    ) {
      change(`${field}.relationship`, 'default');
    }

    if (
      isAffordable &&
      currentHOHIndexes.length === 0 &&
      !isGuarantorLeaseSignerType &&
      !isLiveInCaretakerType &&
      applicantTypeList.find((t) => t.value === value)?.financiallyResponsible
    ) {
      const headOfHouseholdOption =
        affordableRelationshipBundle?.processedAffordableRelationships?.find(
          (r) => r.text?.toLowerCase().includes('head of household'),
        );
      if (headOfHouseholdOption) {
        change(`${field}.affordableRelationship`, headOfHouseholdOption.value);
      }
    }

    prevTypeRef.current = value;
  };

  const safeRelationshipList = relationshipList.map((rel) => {
    const isGuarantorRelation = rel.text?.toLowerCase() === 'guarantor';
    return {
      ...rel,
      disabled: isEditMode && isGuarantorRelation,
    };
  });

  const requiredIndicator = ifElse(
    equals(true),
    always('*'),
    always(''),
  )(showApplicantType);

  const fieldAffordableRelationshipShow = affordableNonAffordableProspect
    ? showAffordableRelation
    : isAffordable;
  return (
    <>
      <Row>
        <div className="labels-head container-of-inputs">
          {showApplicantType && (
            <Col xs={6} sm={2}>
              <label>
                <FormattedMessage {...messages.applicantType} />
                {requiredIndicator}
              </label>
            </Col>
          )}
          <Col xs={6} sm={1}>
            <label>
              <FormattedMessage {...messages.firstName} />
              {requiredIndicator}
            </label>
          </Col>
          <Col xs={6} sm={1}>
            <label>
              <FormattedMessage {...messages.middleName} />
            </label>
          </Col>
          <Col xs={6} sm={1}>
            <label>
              <FormattedMessage {...messages.lastName} />
              {requiredIndicator}
            </label>
          </Col>
          <Col xs={6} sm={1}>
            <label>
              <FormattedMessage {...messages.nameSuffix} />
            </label>
          </Col>
          <Col xs={6} sm={isAffordable ? 1 : 2}>
            <label>
              <FormattedMessage {...messages.preferredName} />
            </label>
          </Col>
          {!isCreateApplicationMode && (
            <Col xs={6} sm={1} className="text-center">
              <label>Primary/HOH</label>
            </Col>
          )}
          {fieldAffordableRelationshipShow ? (
            <Col
              xs={6}
              sm={additionalPhoneAndEmailFieldsForLeadProfile ? 2 : 1}
            >
              <label>
                <FormattedMessage {...messages.affordableRelation} />
                {requiredIndicator}
              </label>
            </Col>
          ) : (
            <></>
          )}
          <Col xs={6} sm={1}>
            <label>
              <FormattedMessage {...messages.relationShip} />
              {requiredIndicator}
            </label>
          </Col>
          {!additionalPhoneAndEmailFieldsForLeadProfile && (
            <>
              <Col xs={6} sm={1}>
                <label>
                  <FormattedMessage {...messages.phone} />
                  {requiredIndicator}
                </label>
              </Col>
              <Col
                xs={6}
                sm={!isProspectMode && !isCreateApplicationMode ? 1 : 2}
              >
                <label>
                  <FormattedMessage {...messages.email} />
                  {localIsProspect ? requiredIndicator : null}
                </label>
              </Col>
            </>
          )}
        </div>
        {!showEditButton && (
          <div className="container-of-trash">
            <div className="row-remove-btn">
              <label>
                <FormattedMessage {...messages.delete} />
              </label>
            </div>
          </div>
        )}
      </Row>
      <Row>
        <div className="container-of-inputs">
          {showApplicantType && (
            <Col xs={6} sm={2}>
              <Field
                name={`${field}.type`}
                component={renderSelectField}
                options={filteredApplicantTypeList}
                disabled={leaseLocked}
                onChange={onTypeChange}
              />
            </Col>
          )}
          <Col xs={6} sm={1}>
            <Field
              name={`${field}.firstName`}
              component={renderTextField}
              maxLength="35"
              disabled={disableFields || leaseLocked}
            />
          </Col>
          <Col xs={6} sm={1}>
            <Field
              name={`${field}.middleName`}
              component={renderTextField}
              maxLength="35"
              disabled={disableFields || leaseLocked}
            />
          </Col>
          <Col xs={6} sm={1}>
            <Field
              name={`${field}.lastName`}
              component={renderTextField}
              maxLength="35"
              disabled={disableFields || leaseLocked}
            />
          </Col>
          <Col xs={6} sm={1}>
            <Field
              name={`${field}.suffixId`}
              component={renderSelectField}
              options={suffixList}
              disabled={disableFields || leaseLocked}
            />
          </Col>
          <Col xs={6} sm={isAffordable ? 1 : 2}>
            <Field
              name={`${field}.preferredName`}
              component={renderTextField}
              maxLength="35"
              disabled={disableFields || leaseLocked}
            />
          </Col>
          {!isCreateApplicationMode && (
            <Col xs={6} sm={1} className="text-center">
              <Field
                key={`is-primary-radio-${index}`}
                name={'primaryApplicantId'}
                option={{ value: id }}
                component={renderRadioButton}
                disabled={
                  disablePrimaryCheckbox ||
                  isNew ||
                  (!isFinancial && showApplicantType) ||
                  isCreateApplicationMode
                }
                props={{
                  input: {
                    name: 'primaryApplicantId',
                    onChange: () => {
                      onPrimaryChange(id, field);
                    },
                    value: primaryApplicantId,
                  },
                }}
              />
            </Col>
          )}
          {fieldAffordableRelationshipShow ? (
            <Col
              xs={6}
              sm={additionalPhoneAndEmailFieldsForLeadProfile ? 2 : 1}
            >
              <Field
                name={`${field}.affordableRelationship`}
                component={renderSelectField}
                options={
                  affordableRelationshipBundle.processedAffordableRelationships
                }
                disabled={
                  affordableRelationshipBundle.disabled ||
                  isGuarantorOrLiveInCaretakerLeaseSigner
                }
              />
            </Col>
          ) : (
            <></>
          )}
          <Col xs={6} sm={1}>
            <Field
              name={`${field}.relationship`}
              component={renderSelectField}
              options={safeRelationshipList}
              disabled={
                isPreviousPrimary
                  ? false
                  : localIsProspect ||
                    leaseLocked ||
                    isGuarantorOrLiveInCaretakerLeaseSigner
              }
            />
          </Col>
          {!additionalPhoneAndEmailFieldsForLeadProfile && (
            <>
              <Col xs={6} sm={1}>
                <Field
                  name={`${field}.phone`}
                  component={renderPhoneFieldWithPermanentWarn}
                  parse={phoneParser}
                  format={phoneFormatter}
                  disabled={disableFields || leaseLocked}
                />
              </Col>
              <Col
                xs={6}
                sm={!isProspectMode && !isCreateApplicationMode ? 1 : 2}
              >
                <Field
                  name={`${field}.email`}
                  component={renderEmailField}
                  maxLength="70"
                  disabled={disableFields || leaseLocked}
                />
              </Col>
            </>
          )}
        </div>
        <div className="container-of-trash">
          <div className="row-remove-btn">
            {showEditButton && (
              <a onClick={onEdit} disabled={isNew || leaseLocked}>
                <i className="et-pencil" />
              </a>
            )}
            <a onClick={onDelete} disabled={localIsProspect || leaseLocked}>
              <i className="et-trash" />
            </a>
          </div>
        </div>
      </Row>
      {additionalPhoneAndEmailFieldsForLeadProfile && (
        <>
          <Row>
            <div className="container-of-inputs">
              <Col xs={5} sm={5}>
                <label>
                  <FormattedMessage {...messages.phone} />
                  {requiredIndicator}
                </label>
              </Col>
              <Col xs={5} sm={5}>
                <label>
                  <FormattedMessage {...messages.email} />
                  {requiredIndicator}
                </label>
              </Col>
            </div>
          </Row>
          <Row>
            <div className="container-of-inputs">
              <Col xs={5} sm={5}>
                <FieldArray
                  name={`${field}.phones`}
                  component={PhoneFields}
                  field={field}
                  disableFields={disableFields}
                  leaseLocked={leaseLocked}
                  isProspectMode={isProspectMode}
                  intl={intl}
                  change={change}
                  required
                  contactId={contactId}
                />
              </Col>
              <Col xs={5} sm={5}>
                <FieldArray
                  name={`${field}.emails`}
                  component={EmailFields}
                  field={field}
                  disableFields={disableFields}
                  leaseLocked={leaseLocked}
                  isProspectMode={isProspectMode}
                  isCreateApplicationMode={isCreateApplicationMode}
                  intl={intl}
                  change={change}
                  required
                  contactId={contactId}
                />
              </Col>
            </div>
          </Row>
          <Divider variant="dashed" style={{ marginBottom: 12 }} />
        </>
      )}
    </>
  );
};

export default Adult;
